// enroll.js
import cookies from 'js-cookie';
import { select, selectAll, each } from '../shared/utils/dom';
import { delimiters } from '../shared/constants';
import { autopayApi, portalSharedApi, removeErrors, showSuccessErrorMessage, achPaymentApi, showError } from './api';
import bankAccountMixin from '../shared/vue/mixins/bankAccount';
import { createError, removeError } from '../shared/formErrors';
import multistepMixin from '../shared/vue/mixins/multistep';
import formMixin from '../shared/vue/mixins/form';
import dataLayerPush from '../shared/utils/analytics';
import { formatBasicCurrency } from '../shared/utils/misc';

const achThresholdCookieName = 'achValidationThresholdHasBeenExceeded';
/**
 * Enrolling in auto pay
 */
export default function vueInit() {
  const el = select('[data-vue-app="enroll"]');
  if (el) {
    const app = new Vue({ // eslint-disable-line no-unused-vars
      el,
      delimiters,
      mixins: [bankAccountMixin, multistepMixin, formMixin],
      data: {
        errorMessage: null,
        account: '',
        accountNumber: '',
        accountType: 'Checking',
        agree: false,
        agreeACH: false,
        date: '20',
        // for accessibility in live regions
        liveText: '',
        modalIsOpen: false,
        paymentDay: 'due',
        routingNumber: '',
        reenterAccountNumber: '',
        success: false,
        isEnrolled: false,
        cancelSuccess: false,
        unenrollSuccess: false,
        canEdit: false,
        shouldPrefill: true,
        showCancelOneTimePaymentCta: false,
        updateExistingOneTimePayment: false,
        updateScheduledAutoPay: false,
        updatePendingPaymentFailed: false,
        amountDue: 0.0,
        agreeOneTime: true,

        // one of ('new', 'edit', 'readonly')
        editMode: 'new',
        validationMessage: '',
        valid: false,
        achCounter: 0,
        achThresholdExceeded: false,
      },
      computed: {
        showWayfinding() {
          return !this.success && !this.isEnrolled;
        },
        showAccountForm() {
          return this.account === 'new' || (this.editMode === 'new' && this.accountNumber);
        },
        paymentDayText() {
          if (this.paymentDay === 'due') {
            return "Billing Due Date";
          }
          return "Five Days Prior to Due Date";
        },
        autoPayUseBillDueDate() {
          return this.paymentDay === 'due';
        },
        formattedAmountDue() {
          return formatBasicCurrency(this.amountDue);
        },
        dayOnly() {
          if (!this.date) {
            return '';
          }
          let dayOnly = this.date.split(' ')[1];
          dayOnly = dayOnly.replace(',', '');
          if (dayOnly.length === 2 && dayOnly[1] === '1') {
            dayOnly += 'st';
          } else if (dayOnly.length === 2 && dayOnly[1] === '2') {
            dayOnly += 'nd';
          } else if (dayOnly.length === 2 && dayOnly[1] === '3') {
            dayOnly += 'rd';
          } else {
            dayOnly += 'th';
          }
          return dayOnly;
        },
      },
      created() {
        Event.$on('A11yDialogConfirm:cancel-autopay', () => {
          this.confirmCancelAutopay(false);
        });
        Event.$on('A11yDialogConfirm:unenroll-autopay', () => {
          this.confirmCancelAutopay(true);
        });
        Event.$on('UpdateAutopayUpdateExistingOneTimePayment', (response) => {
          this.updateExistingOneTimePayment = response.updateExistingOneTimePayment;
          this.loading = true;
          this.postFormContents();
        });
      },
      mounted() {
        this.loading = true;
        // Initialize data from server for updating enrollment
        this.account = (this.$el.dataset && this.$el.dataset.initBankAccountId && this.$el.dataset.initBankAccountId !== "0") ? this.$el.dataset.initBankAccountId : '';
        this.accountNumber = (this.$el.dataset && this.$el.dataset.initBankAccountNumber) ? this.$el.dataset.initBankAccountNumber : '';
        this.accountType = (this.$el.dataset && this.$el.dataset.initAccountType) ? this.$el.dataset.initAccountType : 'Checking';
        this.paymentDay = (this.$el.dataset && this.$el.dataset.initPaymentDay) ? this.$el.dataset.initPaymentDay : 'due';
        this.routingNumber = (this.$el.dataset && this.$el.dataset.initRoutingNumber) ? this.$el.dataset.initRoutingNumber : '';
        this.isEnrolled = (this.$el.dataset && this.$el.dataset.initIsEnrolled) ? this.$el.dataset.initIsEnrolled : false;
        this.canEdit = (this.$el.dataset && this.$el.dataset.canEdit) ? JSON.parse(this.$el.dataset.canEdit) : false;
        this.shouldPrefill = (this.$el.dataset && this.$el.dataset.shouldPrefill) ? JSON.parse(this.$el.dataset.shouldPrefill) : true;
        this.showCancelOneTimePaymentCta = (this.$el.dataset && this.$el.dataset.showCancelOneTimePaymentCta) ? JSON.parse(this.$el.dataset.showCancelOneTimePaymentCta) : false;
        this.achThresholdExceeded = cookies.get(achThresholdCookieName) ? cookies.get(achThresholdCookieName) : false;
        this.updateScheduledAutoPay = (this.$el.dataset && this.$el.dataset.canUpdateScheduledAutoPay) ? JSON.parse(this.$el.dataset.canUpdateScheduledAutoPay) : false;
        this.amountDue = (this.$el.dataset && this.$el.dataset.amountDue) ? JSON.parse(this.$el.dataset.amountDue) : 0.0;
        if (this.amountDue > 0) {
          this.agreeOneTime = false;
        }

        this.editMode = 'readonly';
        if (!this.isEnrolled) {
          this.editMode = 'new';
        } else if (this.canEdit) {
          this.editMode = 'edit';
        }

        if (this.editMode === 'readonly') {
          this.setStep(3);
        }

        Vue.nextTick(() => {
          this.loading = false;
        });
      },
      watch: {
        step(newVal, oldVal) { // eslint-disable-line no-unused-vars
          /**
           * Changes live text depending on the form step
           */
          if (this.step === 2) {
            this.liveText = 'Moving to Step 2 to confirm your account information';
          } else if (this.step === 1) {
            this.liveText = 'You are now back on Step 1 to look up your account';
          }
        },
      },
      methods: {
        initCancel() {
          this.setStep(3);
        },
        postFormContents() {
          this.loading = true;
          this.updatePendingPaymentFailed = false;
          const postPromise = this.editMode === 'new' ? autopayApi.enroll(this) : autopayApi.updateEnrollment(this);
          postPromise.then(() => {
            this.success = true;
            this.setStep(4);
            this.loading = false;
            this.isEnrolled = true;
            // update portal principle after enrolling
            portalSharedApi.updateClaims()
              .catch(err => {
                console.log(err);
              });
            // set the status bubble in the sidebar (multiple)
            each(selectAll('.js-autopay-enrolled'), (statusBubble) => {
              if (statusBubble) {
                statusBubble.classList.add('status-bubble--on');
              }
            });
            dataLayerPush('AutopayEnroll');
          })
            .catch((err) => {
              if (err.response && err.response.data && err.response.data.error && err.response.data.error.code && err.response.data.error.code === 2016 && (this.updateScheduledAutoPay || this.updateExistingOneTimePayment)) {
                this.updatePendingPaymentFailed = true;
                removeErrors();
                setTimeout(this.reloadPage, 15000);
              }
              this.previousStep();
              this.success = false;
              // this.isEnrolled = false;
              this.loading = false;
            });
        },
        reloadPage() {
          location.reload();
        },
        handleClickOnContinue(e) {
          // when the user clicks on continue button, validate there's a value in the input field. if empty, show error
          // if there are no errors, let the user proceed.
          e.preventDefault();
          let hasError = false;
          this.unenrollSuccess = false;

          /*
          * Step 1 validation
          */

          // only validate new bank account information if it's shown
          if (this.account === 'new') {
            if (!this.routingNumber) {
              createError(this.$refs.routingNumber, this.$refs.routingNumberError, 'Please enter a routing number.');
              hasError = true;
            } else {
              removeError(this.$refs.routingNumber, this.$refs.routingNumberError);
            }

            if (!this.accountNumber) {
              createError(this.$refs.accountNumber, this.$refs.accountNumberError, 'Please enter an account number.');
              hasError = true;
            } else {
              removeError(this.$refs.accountNumber, this.$refs.accountNumberError);
            }
            if (!this.reenterAccountNumber) {
              createError(this.$refs.reenterAccountNumber, this.$refs.reenterAccountNumberError, 'Please reenter the account number.');
              hasError = true;
            } else if (this.accountNumber !== this.reenterAccountNumber) {
              createError(this.$refs.reenterAccountNumber, this.$refs.reenterAccountNumberError, 'Account numbers do not match.');
              hasError = true;
            } else {
              removeError(this.$refs.reenterAccountNumber, this.$refs.reenterAccountNumberError);
            }
          }

          if (hasError) {
            // update live text to let the user know there are errors
            this.liveText = 'The form has errors. Please make sure your payment information is correct.';
          } else if (this.account === 'new') {
            this.loading = true;
            this.submitting = true;
            achPaymentApi.validateAccountNumber(this, true, false)
              .then((response) => {
                this.loading = false;
                this.submitting = false;
                this.validationMessage = response.data.validationMessage;
                this.valid = response.data.valid;
                if (!response.data.uSBankDown) {
                  if (this.valid) {
                    this.nextStep(e);
                  } else {
                    const message = { PrimaryMessage: "We're Sorry", SecondaryMessage: this.validationMessage };
                    showError("ach-payment-error", message);
                    this.achCounter += 1;
                    if (this.achCounter >= 3) {
                      this.sendAchRemark();
                      this.achThresholdExceeded = true;
                      cookies.set(achThresholdCookieName, true);
                      Event.$emit('A11yDialogOpen', { dialogKey: 'ach-validation-threshold-exceeded' });
                    }
                  }
                } else {
                  this.nextStep(e);
                }
              })
              .catch(() => {
                this.loading = false;
                this.submitting = false;
                removeErrors();
                showSuccessErrorMessage(this.errorMessages, "OTP-06");
              });
          } else {
            this.nextStep(e);
          }
        },
        sendAchRemark() {
          this.loading = true;
          this.submitting = true;
          achPaymentApi.sendAchRemark(this, true, false)
            .then(() => {
              this.loading = false;
              this.submitting = false;
            })
            .catch(() => {
              this.loading = false;
              this.submitting = false;
              removeErrors();
              showSuccessErrorMessage(this.errorMessages, "OTP-06");
            })
        },
        confirmCancelAutopay(unenroll) {
          this.loading = true;
          autopayApi.cancelAutopay()
            .then(() => {
              this.loading = false;
              this.editMode = 'new';
              this.accountNumber = '';
              this.routingNumber = '';

              if (unenroll) {
                this.unenrollSuccess = true;
              } else {
                this.cancelSuccess = true;
              }

              this.isEnrolled = false;
              dataLayerPush('AutopayUnenroll');
              // set the status bubble in the sidebar (multiple)
              each(selectAll('.js-autopay-enrolled'), (statusBubble) => {
                if (statusBubble) {
                  statusBubble.classList.remove('status-bubble--on');
                }
              });
              // update portal principle after cancel
              portalSharedApi.updateClaims()
                .catch(err => {
                  console.log(err);
                });
            })
            .catch(() => {
              this.loading = false;
              this.cancelSuccess = false;
            });
        },
        handleSelectAnAccount(e) {
          this.getPayFrom(e);

          if (this.account === 'new') {
            this.accountType = 'Checking';
            this.accountNumber = '';
            this.routingNumber = '';
          }
        },
      },
    });
  }
}



// WEBPACK FOOTER //
// ./src/assets/toolkit/scripts/portal/autopay.js